import React, { useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { UserPlus, Loader2 } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { name: "", email: "", password: "", plan: "trial", company_name: "" };

export const AdminCreateUser = ({ planOpts, onCreated }) => {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const save = async () => {
    if (!f.name.trim() || !f.email.trim()) return toast.error("Informe nome e e-mail");
    if (f.password.length < 6) return toast.error("Senha deve ter pelo menos 6 caracteres");
    setBusy(true);
    try {
      await api.post("/admin/users", f);
      toast.success("Conta criada! O usuário já pode entrar com e-mail e senha.");
      setOpen(false); setF(EMPTY); onCreated?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro ao criar conta"); } finally { setBusy(false); }
  };

  return (
    <>
      <Button className="h-9" onClick={() => setOpen(true)} data-testid="admin-create-user-btn"><UserPlus className="h-4 w-4 mr-1" /> Criar usuário</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="admin-create-user-dialog">
          <DialogHeader><DialogTitle>Criar nova conta</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Nome</Label><Input value={f.name} onChange={set("name")} className="h-11" data-testid="admin-new-name" /></div>
            <div><Label>E-mail</Label><Input type="email" value={f.email} onChange={set("email")} className="h-11" data-testid="admin-new-email" /></div>
            <div><Label>Senha inicial</Label><Input type="text" value={f.password} onChange={set("password")} placeholder="mínimo 6 caracteres" className="h-11" data-testid="admin-new-password" /></div>
            <div><Label>Empresa (opcional)</Label><Input value={f.company_name} onChange={set("company_name")} className="h-11" data-testid="admin-new-company" /></div>
            <div><Label>Plano</Label>
              <Select value={f.plan} onValueChange={(v) => setF({ ...f, plan: v })}>
                <SelectTrigger className="h-11" data-testid="admin-new-plan"><SelectValue /></SelectTrigger>
                <SelectContent>{planOpts.map(([p, l]) => <SelectItem key={p} value={p}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={save} disabled={busy} data-testid="admin-new-save">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Criar conta"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

const ACTION = { create: ["Conta criada", "bg-emerald-500/10 text-emerald-700"], plan_change: ["Plano alterado", "bg-primary/10 text-primary"], block: ["Bloqueado", "bg-red-500/10 text-red-700"], unblock: ["Liberado", "bg-sky-500/10 text-sky-700"], delete: ["Excluído", "bg-slate-200 text-slate-700"] };

export const AdminLogs = ({ logs }) => (
  <div className="space-y-1.5" data-testid="admin-logs">
    {logs.length === 0 && <p className="text-sm text-muted-foreground py-4 text-center">Nenhuma ação registrada ainda.</p>}
    {logs.map((l) => {
      const [label, cls] = ACTION[l.action] || [l.action, "bg-muted"];
      return (
        <div key={l.id} className="flex items-center gap-3 p-2.5 rounded-lg border border-border/60 text-sm" data-testid={`admin-log-${l.id}`}>
          <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full shrink-0 ${cls}`}>{label}</span>
          <div className="min-w-0 flex-1"><p className="truncate font-medium">{l.target_email}</p><p className="text-xs text-muted-foreground truncate">{l.detail}</p></div>
          <span className="text-xs text-muted-foreground whitespace-nowrap">{new Date(l.at).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" })}</span>
        </div>
      );
    })}
  </div>
);
